'use client';

import { useEffect, useMemo } from 'react';

import { FormControl, Label, Stack, TextField, Text } from '@backyard-ui/core';
import * as tracker from '@leroy-merlin-br/backpack-analytics';

import { algolia } from '@/infra/tracking/algolia';
import { pushCalculoFretePdp } from '@/infra/tracking/events';
import {
  RecontextAlertDialog,
  useRegionChecker,
  useZipCode,
} from '@/presenters/components/common/ZipCode';
import { useProductStore } from '@/presenters/store/catalog/product';

import { UNKNOWN_ZIP_CODE_LABEL } from './constants';
import { DisabledDisclaimer } from './DisabledDisclaimer';
import { ErrorDisclaimer } from './ErrorDisclaimer';
import { HelperText } from './HelperText';
import { Modalities } from './Modalities';
import { ModalitiesModal } from './ModalitiesModal';
import useShipments from './useShipments';
import useStatus from './useStatus';

export default function Shipments() {
  const productId = useProductStore((store) => store.product._id);
  const currentSeller = useProductStore((store) => store.currentSeller);
  const {
    ecommerceScopes: { canPurchase },
  } = useProductStore((store) => store.currentSeller);

  const zipCodeIsDisabled = !canPurchase;

  const { zipCode, zipCodeIsValid, onChangeZipCode, clearZipCode } = useZipCode(
    !zipCodeIsDisabled
  );

  const {
    data: region,
    isFetching: regionCheckerIsFetching,
    recontext,
    status: regionCheckerStatus,
    isError: regionCheckerIsError,
    isFetched: regionCheckerIsFetched,
  } = useRegionChecker({
    zipCode,
    zipCodeIsValid,
  });

  const {
    data: shipments,
    isFetching: shipmentsIsFetching,
    isError: shipmentsIsError,
    isFetched: shipmentsIsFetched,
  } = useShipments(
    {
      zipCode,
      productId,
    },
    { enabled: regionCheckerStatus === 'valid' }
  );

  const isError = shipmentsIsError || regionCheckerIsError;
  const isFetching = shipmentsIsFetching || regionCheckerIsFetching;
  const isFetched = shipmentsIsFetched || regionCheckerIsFetched;

  const status = useStatus({
    zipCode,
    zipCodeIsValid,
    isFetching,
    isError,
  });

  useEffect(() => {
    if (!isFetching && isFetched && !isError) {
      const cheapestModality =
        shipments?.data[currentSeller.id]?.chepeastModality;

      pushCalculoFretePdp({
        cheapestModality,
        region: {
          city: region?.data.city,
          stateCode: region?.data.stateCode,
        },
        shopName: currentSeller.shop.name,
        zipCode,
      });

      tracker.pushShippingCalculation({
        pageType: 'product',
        shipping: {
          city: region?.data.city,
          stateCode: region?.data.stateCode,
          zipCode: zipCode,
          price: cheapestModality?.cost || 0,
          deliveryTime: cheapestModality?.deadline,
          seller: currentSeller.shop.name,
          items: [productId],
        },
      });
    }
  }, [
    isFetching,
    isFetched,
    isError,
    productId,
    currentSeller,
    zipCode,
    region,
    shipments,
  ]);

  const content = useMemo(() => {
    if (zipCodeIsDisabled) {
      return <DisabledDisclaimer />;
    }

    if (!isFetching && isFetched) {
      if (isError) {
        return <ErrorDisclaimer />;
      }

      return (
        <>
          <Modalities />
          <ModalitiesModal />
        </>
      );
    }

    return null;
  }, [isFetching, isError, zipCodeIsDisabled, isFetched]);

  return (
    <Stack
      className="w-full rounded border border-gray-300 p-4"
      direction="column"
      justify="flex-start"
      spacing="4"
    >
      <RecontextAlertDialog
        status={regionCheckerStatus}
        onCancel={clearZipCode}
        onConfirm={recontext}
      />
      <Stack align="center">
        <FormControl.Root
          isInvalid={status.state === 'invalid'}
          isDisabled={zipCodeIsDisabled}
        >
          <Label>Calcule seu frete</Label>

          <TextField.Root>
            <TextField.Input
              type="text"
              placeholder="Digite seu CEP..."
              value={zipCode}
              onFocus={() => {
                algolia.sendPdpClickEvent();
              }}
              onChange={onChangeZipCode}
              UNSAFE_className="rounded"
            />
          </TextField.Root>

          <FormControl.HelperText>
            <HelperText status={status} />
          </FormControl.HelperText>
        </FormControl.Root>

        <Text
          size="md"
          color={zipCodeIsDisabled ? 'gray-300' : 'green-700'}
          UNSAFE_className="shrink-0"
          weight="bold"
          asChild
        >
          {zipCodeIsDisabled ? (
            <span>{UNKNOWN_ZIP_CODE_LABEL}</span>
          ) : (
            <a
              href="https://buscacepinter.correios.com.br/app/endereco/"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => algolia.sendPdpClickEvent()}
            >
              {UNKNOWN_ZIP_CODE_LABEL}
            </a>
          )}
        </Text>
      </Stack>
      {content}
    </Stack>
  );
}
